import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  DestinationFilterInput,
  useSearchDestinationsLazyQuery,
} from "../../../../../generated/graphql";
import { AsyncSelect } from "../../async-select/AsyncSelect";
import { AsyncSelectData } from "../../async-select/AsyncSelectData";
import { useTranslation } from "react-i18next";
import AuthContext from "src/app/contexts/auth-context";

interface FormikFormSelectProps {
  fieldName: string;
  labelName: string;
  intialId?: number;
  formik;
  type?: string;
}

const SEARCH_DEBOUNCE_MS = 300;
const DESTINATION_RESULT_LIMIT = 15;

type LoadDestinationsArgs = {
  searchValue: string;
  specificDestinationId?: number;
  limit?: number;
  isLoadMore?: boolean;
};

const FormikCustomerDestinationFormSelect = (props: FormikFormSelectProps) => {
  const authContext = useContext(AuthContext);
  const companyId = Number(
    authContext?.authContextData?.currentUser?.companyId ??
      authContext?.authContextData?.currentUser?.location?.companyId ??
      authContext?.authContextData?.currentUser?.location?.company?.id ??
      0
  );
  const [searchDestinations] = useSearchDestinationsLazyQuery();
  const formikSetFieldValue = props.formik.setFieldValue;

  const { t } = useTranslation();

  const [initialCustomerDestination, setInitialCustomerDestination] =
    useState<AsyncSelectData>();
  const [destinationOptions, setDestinationOptions] = useState<
    AsyncSelectData[]
  >([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResultLimit, setSearchResultLimit] = useState(
    DESTINATION_RESULT_LIMIT
  );
  const [loadingDestinations, setLoadingDestinations] = useState(false);
  const [loadingMoreDestinations, setLoadingMoreDestinations] = useState(false);
  const [hasMoreDestinations, setHasMoreDestinations] = useState(false);
  const hasAppliedInitialSelection = useRef(false);
  const latestListQueryVersion = useRef(0);

  const mapDestinationOptions = useCallback(
    (
      destinations?:
        | { id: number; code: string; name?: string | null }[]
        | null
    ) => {
      return (
        destinations?.map((destination) => {
          const title = destination.name
            ? `${destination.code} - ${destination.name}`
            : destination.code;

          return new AsyncSelectData({
            title,
            value: destination.id,
          });
        }) ?? []
      );
    },
    []
  );

  const loadDestinations = useCallback(
    async ({
      searchValue,
      specificDestinationId,
      limit = DESTINATION_RESULT_LIMIT,
      isLoadMore = false,
    }: LoadDestinationsArgs) => {
      if (companyId <= 0) {
        if (!specificDestinationId) {
          setDestinationOptions([]);
          setHasMoreDestinations(false);
        }
        return [] as AsyncSelectData[];
      }

      const trimmedSearch = searchValue.trim();
      let where: DestinationFilterInput | undefined;

      if (!specificDestinationId) {
        if (isLoadMore) {
          setLoadingMoreDestinations(true);
        } else {
          setLoadingDestinations(true);
        }
      }

      const queryVersion = specificDestinationId
        ? latestListQueryVersion.current
        : ++latestListQueryVersion.current;

      if (specificDestinationId) {
        where = { id: { eq: specificDestinationId } };
      } else if (trimmedSearch.length > 0) {
        where = {
          or: [
            { code: { contains: trimmedSearch } },
            { name: { contains: trimmedSearch } },
          ],
        };
      }

      try {
        const { data } = await searchDestinations({
          variables: {
            companyId,
            where,
            first: limit,
          },
          fetchPolicy: "network-only",
        });

        const options = mapDestinationOptions(data?.destinations?.nodes);

        if (!specificDestinationId) {
          if (queryVersion === latestListQueryVersion.current) {
            setDestinationOptions(options);
            setSearchResultLimit(limit);
            setHasMoreDestinations(options.length >= limit);
          }
        }

        return options;
      } catch {
        if (!specificDestinationId) {
          if (queryVersion === latestListQueryVersion.current) {
            setDestinationOptions([]);
            setHasMoreDestinations(false);
          }
        }

        return [] as AsyncSelectData[];
      } finally {
        if (!specificDestinationId) {
          if (isLoadMore) {
            setLoadingMoreDestinations(false);
          } else if (queryVersion === latestListQueryVersion.current) {
            setLoadingDestinations(false);
          }
        }
      }
    },
    [companyId, mapDestinationOptions, searchDestinations]
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      loadDestinations({
        searchValue: searchTerm,
        limit: DESTINATION_RESULT_LIMIT,
      });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loadDestinations, searchTerm]);

  useEffect(() => {
    hasAppliedInitialSelection.current = false;
    setInitialCustomerDestination(undefined);
  }, [props.fieldName, props.intialId]);

  useEffect(() => {
    if (hasAppliedInitialSelection.current) {
      return;
    }

    if (!props.intialId) {
      setInitialCustomerDestination(undefined);
      hasAppliedInitialSelection.current = true;
      return;
    }

    const selectedDestination = destinationOptions.find(
      (option) => Number(option.value) === props.intialId
    );

    if (selectedDestination) {
      setInitialCustomerDestination(selectedDestination);
      formikSetFieldValue(props.fieldName, Number(selectedDestination.value));
      hasAppliedInitialSelection.current = true;
      return;
    }

    let active = true;
    loadDestinations({
      searchValue: "",
      specificDestinationId: props.intialId,
    }).then((options) => {
      if (!active || hasAppliedInitialSelection.current) {
        return;
      }

      const initialDestination = options[0];
      if (initialDestination) {
        setInitialCustomerDestination(initialDestination);
        formikSetFieldValue(props.fieldName, Number(initialDestination.value));
      }

      hasAppliedInitialSelection.current = true;
    });

    return () => {
      active = false;
    };
  }, [
    destinationOptions,
    formikSetFieldValue,
    loadDestinations,
    props.fieldName,
    props.intialId,
  ]);

  const selectedCustomerDestinationChanged = useCallback(
    (data: AsyncSelectData) => {
      formikSetFieldValue(props.fieldName, Number(data?.value ?? 0));
    },
    [formikSetFieldValue, props.fieldName]
  );

  const handleDestinationInputChanged = useCallback(
    (_evt: any, value: string) => {
      setSearchTerm(value ?? "");
    },
    []
  );

  const handleDestinationLoadMore = useCallback(() => {
    if (
      loadingDestinations ||
      loadingMoreDestinations ||
      !hasMoreDestinations
    ) {
      return;
    }

    void loadDestinations({
      searchValue: searchTerm,
      limit: searchResultLimit + DESTINATION_RESULT_LIMIT,
      isLoadMore: true,
    });
  }, [
    hasMoreDestinations,
    loadDestinations,
    loadingDestinations,
    loadingMoreDestinations,
    searchResultLimit,
    searchTerm,
  ]);


  return (
    <>
      <div className={`mt-3`}>
        <AsyncSelect
          label={t("modules.formik.destinations")}
          onItemSelected={selectedCustomerDestinationChanged}
          inputValueChanged={handleDestinationInputChanged}
          onLoadMore={handleDestinationLoadMore}
          loadingMoreData={loadingMoreDestinations}
          defaultSelectOption={initialCustomerDestination}
          data={destinationOptions}
          loadingData={loadingDestinations}
          hasError={Boolean(
            props.formik.errors[props.fieldName] &&
            props.formik.touched[props.fieldName]
          )}
          className={`w-full`}
        />
      </div>
    </>
  );
};

export { FormikCustomerDestinationFormSelect };

